/**
 * W3C `traceparent` generator for SPA-originated fetches.
 *
 * Mirror of lexide/src/lib/traceparent.ts — kept duplicated until
 * `@lexiun/lx-obs/browser` (deferred follow-up) makes a shared
 * browser-safe helper available to all SPAs.
 *
 * Format: `00-<32 hex trace_id>-<16 hex span_id>-<flags>`
 * Spec:   https://www.w3.org/TR/trace-context/#traceparent-header
 *
 * Lexserver's `sentry-scope` middleware calls extractTraceparentScope()
 * on inbound requests and threads the resulting trace_id through
 * lx-obs `obs.withScope` — so one user click in the SPA produces a
 * single trace_id across every operational-log entry it generates.
 * Without this header lexserver mints an orphan trace per call.
 *
 * Strategy: fresh trace_id per fetch. Each user action is one logical
 * operation; a single page-load produces independent traces.
 */

const HEX_TABLE = Array.from({ length: 256 }, (_, i) => i.toString(16).padStart(2, "0"));

function bytesToHex(bytes: Uint8Array): string {
  let out = "";
  for (let i = 0; i < bytes.length; i++) {
    out += HEX_TABLE[bytes[i]!];
  }
  return out;
}

/** Returns a fresh `traceparent` header value. */
export function newTraceparent(): string {
  const traceBytes = new Uint8Array(16);
  const spanBytes = new Uint8Array(8);
  crypto.getRandomValues(traceBytes);
  crypto.getRandomValues(spanBytes);
  return `00-${bytesToHex(traceBytes)}-${bytesToHex(spanBytes)}-01`;
}
